import { useEffect, useState } from "react";
import classNames from "classnames";
import _ from "lodash";

import CentredSpinner from "lib/core_components/CentredSpinner";
import { SignalSettings } from "lib/hooks/api/teams/useSignalSettings";
import CategoryDropdown from "./CategoryDropdown";
import InfiniteSuppliersChecklist from "./InfiniteSuppliersChecklist";
import SignalSuppliersChecklist from "./SignalSupplierChecklist";
import { SupplierOption, SupplierSelectCategory } from "./types";

import css from "./SuppliersDropdown.module.scss";

type Props = {
  competitors: SignalSettings["competitors"];
  partners: SignalSettings["partners"];
  isLoading: boolean;
  handleSuppliersSelection: (suppliers: SupplierOption[]) => void;
  textSearch: string;
  isOpen: boolean;
  selectedSuppliers: SupplierOption[];
};

function SuppliersDropdown({
  competitors,
  partners,
  isLoading,
  handleSuppliersSelection,
  textSearch,
  isOpen,
  selectedSuppliers,
}: Props) {
  // Categories
  const [categorySelected, setCategorySelected] = useState<SupplierSelectCategory>();

  // if modal hidden, reset the category
  useEffect(() => {
    if (!isOpen) {
      setCategorySelected(undefined);
    }
  }, [isOpen]);

  const categoryOptions: SupplierSelectCategory[] = ["Competitor", "Partner", "Other suppliers"];

  const signalSupplierOptions = categorySelected === "Competitor" ? competitors : partners;

  const allSignalSuppliers = _.union(competitors, partners).map((s) => ({
    label: s.name,
    value: s.id,
  }));

  function handleCategorySelection(label: SupplierSelectCategory) {
    setCategorySelected(label);
  }

  function getCategoryLabel(category?: string): string {
    switch (category) {
      case "Competitor":
        return `Competitor signals (${competitors.length})`;
      case "Partner":
        return `Partner signals (${partners.length})`;
      case "Other suppliers":
      default:
        if (isLoading) return `Other suppliers`;
        return `Other suppliers (10,000+)`;
    }
  }

  const renderedCategoryInput = () => {
    if (isLoading) return <CentredSpinner />;

    if (categorySelected === undefined && textSearch.length === 0)
      return (
        <CategoryDropdown
          categories={categoryOptions}
          handleCategorySelection={handleCategorySelection}
          getCategoryLabel={getCategoryLabel}
        />
      );

    if (categorySelected === "Other suppliers" || textSearch.length > 0) {
      return (
        <InfiniteSuppliersChecklist
          signalSuppliers={allSignalSuppliers}
          excludeSignals={textSearch.length === 0}
          textSearch={textSearch}
          onChange={handleSuppliersSelection}
          selectedSuppliers={selectedSuppliers}
          backBtnFn={() => setCategorySelected(undefined)}
        />
      );
    }

    return (
      <SignalSuppliersChecklist
        options={signalSupplierOptions}
        category={categorySelected ?? "Competitor"}
        onChange={handleSuppliersSelection}
        selectedSuppliers={selectedSuppliers}
        backBtnFn={() => setCategorySelected(undefined)}
      />
    );
  };

  return (
    <div
      className={classNames(css.options, {
        [css.show]: isOpen,
      })}
      onMouseDown={(e) => {
        e.preventDefault();
      }}
    >
      {
        // show the description when not searching
        textSearch.length === 0 && (
          <div className={css.filterDescription}>
            <span className={css.filterDescriptionText}>
              Search the suppliers' activities you want to track
            </span>
          </div>
        )
      }
      <div className={css.dropdown}>{renderedCategoryInput()}</div>
    </div>
  );
}

export default SuppliersDropdown;
